<template>
  <CCard class="bg-style2 mb-4">
    <CCardHeader class="bg-gradient-info text-white" style="border-top-left-radius: 1rem; border-top-right-radius: 1rem">
      <span class="font-weight-bold h6">
        <CIcon name="cil-cloud-upload"/>
        Import Account
      </span>
      <div class="card-header-actions">
        <span v-if="summary.totalRows > 0">Rows : {{ summary.totalRows }}</span>
      </div>
    </CCardHeader>
    <CCardBody>
      <CRow class="mb-3">
        <CCol md="8">
          <div
            class="import-dropzone"
            :class="{ 'is-dragging': isDragging }"
            @click="openFileDialog"
            @dragenter.prevent="onDragEnter"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop"
          >
            <input
              ref="fileInput"
              type="file"
              accept=".xlsx,.xls"
              class="d-none"
              @change="onFileChange"
            >
            <div class="font-weight-bold mb-1">เลือกไฟล์ Excel สำหรับ Import Account</div>
            <div class="text-muted small">
              <CIcon name="cil-file" class="mr-1"/>
              รองรับ .xlsx และ .xls จาก HR export หรือ drag & drop ไฟล์ลงกล่องนี้
            </div>
            <div v-if="fileName" class="mt-2 text-info font-weight-bold">
              <CIcon name="cil-description" class="mr-1"/>
              {{ fileName }}
            </div>
          </div>
        </CCol>
        <CCol md="4">
          <CCard class="h-100 border-0 shadow-none import-note">
            <CCardBody class="p-3">
              <div class="font-weight-bold mb-2">
                <CIcon name="cil-list-rich" class="mr-1"/>
                กติกาที่ใช้คัดข้อมูล
              </div>
              <div class="small text-muted"><CIcon name="cil-user" class="mr-1"/>ต้องมี `รหัสบุคลากร`</div>
              <div class="small text-muted"><CIcon name="cil-check-circle" class="mr-1"/>ต้องเป็น `O : เปิด`</div>
              <div class="small text-muted"><CIcon name="cil-address-book" class="mr-1"/>ต้องมีชื่อหรือนามสกุลอย่างน้อยหนึ่งค่า</div>
              <div class="small text-muted"><CIcon name="cil-copy" class="mr-1"/>จะแจ้งรายการซ้ำในไฟล์แยกให้</div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CRow v-if="summary.totalRows > 0" class="mb-3">
          <CCol md="3" sm="6" class="mb-3">
          <CCard class="summary-card border-0">
            <CCardBody class="py-3">
              <div class="text-muted small"><CIcon name="cil-spreadsheet" class="mr-1"/>ทั้งหมด</div>
              <div class="h4 mb-0 font-weight-bold">{{ summary.totalRows }}</div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md="3" sm="6" class="mb-3">
          <CCard class="summary-card border-0">
            <CCardBody class="py-3">
              <div class="text-muted small"><CIcon name="cil-check-alt" class="mr-1"/>พร้อมนำเข้า</div>
              <div class="h4 mb-0 font-weight-bold text-success">{{ summary.readyRows }}</div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md="3" sm="6" class="mb-3">
          <CCard class="summary-card border-0">
            <CCardBody class="py-3">
              <div class="text-muted small"><CIcon name="cil-copy" class="mr-1"/>ซ้ำในไฟล์</div>
              <div class="h4 mb-0 font-weight-bold text-warning">{{ summary.duplicateRows }}</div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md="3" sm="6" class="mb-3">
          <CCard class="summary-card border-0">
            <CCardBody class="py-3">
              <div class="text-muted small"><CIcon name="cil-ban" class="mr-1"/>ข้าม</div>
              <div class="h4 mb-0 font-weight-bold text-danger">{{ summary.skippedRows }}</div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CRow v-if="summary.totalRows > 0" class="mb-3">
        <CCol class="text-right">
          <CButton
            color="info"
            shape="pill"
            variant="outline"
            :disabled="readyItems.length === 0 || importing"
            @click="confirmImport"
          >
            <CIcon name="cil-cloud-upload"/>
            {{ importing ? 'Importing...' : 'Confirm Import' }}
          </CButton>
        </CCol>
      </CRow>

      <CRow v-if="summary.totalRows > 0" class="mb-3">
        <CCol md="6" lg="5">
          <CInput
            v-model="searchText"
            placeholder="ค้นหา รหัสบุคลากร / ชื่อ / ตำแหน่ง / หน่วยงาน"
          >
            <template #prepend-content>
              <CIcon name="cil-search"/>
            </template>
          </CInput>
        </CCol>
      </CRow>

      <CAlert v-if="errorMessage" color="danger" show>
        <CIcon name="cil-warning" class="mr-1"/>
        {{ errorMessage }}
      </CAlert>

      <CAlert v-if="summary.totalRows > 0" color="info" show>
        <CIcon name="cil-lock-locked" class="mr-1"/>
        password เริ่มต้นจะใช้รหัสบุคลากร และระบบจะสร้าง account เฉพาะรายการที่ผ่าน validation
      </CAlert>

      <CAlert v-if="summary.totalRows > 0" :color="summary.readyRows > 0 ? 'success' : 'warning'" show>
        <CIcon :name="summary.readyRows > 0 ? 'cil-check-circle' : 'cil-warning'" class="mr-1"/>
        ไฟล์นี้อ่านได้ {{ summary.totalRows }} แถว และมีรายการพร้อม import {{ summary.readyRows }} แถว
      </CAlert>

      <CAlert v-if="importSummary" :color="importSummary.failed > 0 ? 'warning' : 'success'" show>
        <CIcon name="cil-task" class="mr-1"/>
        Import แล้วทั้งหมด {{ importSummary.total }} รายการ | สำเร็จ {{ importSummary.success }} | ซ้ำ {{ importSummary.duplicate }} | ผิดพลาด {{ importSummary.failed }}
      </CAlert>

      <CAlert v-if="importErrorSummary" color="danger" show>
        <CIcon name="cil-warning" class="mr-1"/>
        {{ importErrorSummary }}
      </CAlert>

      <CTabs v-if="summary.totalRows > 0" add-tab-classes="mt-2">
        <CTab>
          <template slot="title">
            <CIcon name="cil-check-circle"/>
            พร้อมนำเข้า ({{ readyItems.length }})
          </template>
          <CDataTable
            :items="filteredReadyItems"
            :fields="previewFields"
            :items-per-page="10"
            hover
            striped
            bordered
            small
            pagination
          >
            <template #index="{index}">
              <td class="text-center">{{ index + 1 }}</td>
            </template>
          </CDataTable>
        </CTab>
        <CTab>
          <template slot="title">
            <CIcon name="cil-warning"/>
            ซ้ำในไฟล์ ({{ duplicateItems.length }})
          </template>
          <CDataTable
            :items="filteredDuplicateItems"
            :fields="previewIssueFields"
            :items-per-page="10"
            hover
            striped
            bordered
            small
            pagination
          >
            <template #index="{index}">
              <td class="text-center">{{ index + 1 }}</td>
            </template>
          </CDataTable>
        </CTab>
        <CTab>
          <template slot="title">
            <CIcon name="cil-ban"/>
            ข้าม ({{ skippedItems.length }})
          </template>
          <CDataTable
            :items="filteredSkippedItems"
            :fields="previewIssueFields"
            :items-per-page="10"
            hover
            striped
            bordered
            small
            pagination
          >
            <template #index="{index}">
              <td class="text-center">{{ index + 1 }}</td>
            </template>
          </CDataTable>
        </CTab>
        <CTab v-if="importResultItems.length > 0">
          <template slot="title">
            <CIcon name="cil-task"/>
            ผลการ Import ({{ importResultItems.length }})
          </template>
          <CDataTable
            :items="filteredImportResultItems"
            :fields="importResultFields"
            :items-per-page="10"
            hover
            striped
            bordered
            small
            pagination
          >
            <template #index="{index}">
              <td class="text-center">{{ index + 1 }}</td>
            </template>
          </CDataTable>
        </CTab>
      </CTabs>
    </CCardBody>
  </CCard>
</template>

<script>
import * as XLSX from "xlsx";
import Service from "@/service/api";

export default {
  name: "ImportAccountsPanel",
  data() {
    return {
      fileName: "",
      errorMessage: "",
      readyItems: [],
      duplicateItems: [],
      skippedItems: [],
      importing: false,
      isDragging: false,
      importSummary: null,
      importErrorSummary: "",
      searchText: "",
      summary: {
        totalRows: 0,
        readyRows: 0,
        duplicateRows: 0,
        skippedRows: 0,
      },
      previewFields: [
        { key: "index", label: "#", _style: "width:60px; text-align: center" },
        { key: "employeeCode", label: "รหัสบุคลากร", _style: "width:140px;" },
        { key: "fullName", label: "ชื่อ-สกุล" },
        { key: "positionCode", label: "รหัสตำแหน่ง", _style: "width:140px;" },
        { key: "positionName", label: "ตำแหน่ง" },
        { key: "positionStatus", label: "สถานะตำแหน่ง", _style: "width:140px;" },
        { key: "academicTitle", label: "ตำแหน่งทางวิชาการ" },
        { key: "lineTypeName", label: "สายงาน" },
        { key: "employeeTypeName", label: "ประเภทบุคลากร" },
        { key: "groupName", label: "กลุ่มหน่วยงาน" },
        { key: "organizationName", label: "หน่วยงาน" },
        { key: "affiliationName", label: "สังกัด" },
        { key: "departmentName", label: "สาขาวิชา/ฝ่ายงาน" },
        { key: "employmentStatus", label: "สถานภาพ", _style: "width:120px;" },
        { key: "startDate", label: "วันบรรจุ", _style: "width:130px;" },
        { key: "endDate", label: "วันหมดสัญญา", _style: "width:130px;" },
      ],
      previewIssueFields: [
        { key: "index", label: "#", _style: "width:60px; text-align: center" },
        { key: "employeeCode", label: "รหัสบุคลากร", _style: "width:140px;" },
        { key: "fullName", label: "ชื่อ-สกุล" },
        { key: "positionCode", label: "รหัสตำแหน่ง", _style: "width:140px;" },
        { key: "positionName", label: "ตำแหน่ง" },
        { key: "positionStatus", label: "สถานะตำแหน่ง", _style: "width:140px;" },
        { key: "groupName", label: "กลุ่มหน่วยงาน" },
        { key: "organizationName", label: "หน่วยงาน" },
        { key: "departmentName", label: "สาขาวิชา/ฝ่ายงาน" },
        { key: "reason", label: "เหตุผล" },
      ],
      importResultFields: [
        { key: "index", label: "#", _style: "width:60px; text-align: center" },
        { key: "employeeCode", label: "รหัสบุคลากร", _style: "width:140px;" },
        { key: "fullName", label: "ชื่อ-สกุล" },
        { key: "statusLabel", label: "สถานะ", _style: "width:120px;" },
        { key: "reason", label: "รายละเอียด" },
      ],
    };
  },
  computed: {
    filteredReadyItems() {
      return this.filterItems(this.readyItems);
    },

    filteredDuplicateItems() {
      return this.filterItems(this.duplicateItems);
    },

    filteredSkippedItems() {
      return this.filterItems(this.skippedItems);
    },

    importResultItems() {
      var summaryItems = ((((this.importSummary || {}).items) || [])).map((item, index) => {
        var matchedPreviewItem = this.readyItems.find((readyItem) => readyItem.employeeCode === item.employeeCode) || {};

        return {
          index: index + 1,
          employeeCode: this.cleanValue(item.employeeCode),
          fullName: matchedPreviewItem.fullName || "-",
          status: item.status,
          statusLabel: this.getImportStatusLabel(item.status),
          reason: this.formatImportResultReason(item),
        };
      });

      if (summaryItems.length > 0) {
        return summaryItems;
      }

      if (!this.importErrorSummary) {
        return [];
      }

      return [{
        index: 1,
        employeeCode: "-",
        fullName: "-",
        status: "failed",
        statusLabel: this.getImportStatusLabel("failed"),
        reason: this.importErrorSummary,
      }];
    },

    filteredImportResultItems() {
      return this.filterItems(this.importResultItems);
    },
  },
  methods: {
    openFileDialog() {
      this.$refs.fileInput.click();
    },

    onDragEnter() {
      this.isDragging = true;
    },

    onDragOver() {
      this.isDragging = true;
    },

    onDragLeave(event) {
      if (event && event.currentTarget && event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) {
        return;
      }

      this.isDragging = false;
    },

    onDrop(event) {
      this.isDragging = false;
      const file = (((event || {}).dataTransfer || {}).files || [])[0];
      this.handleFile(file);
    },

    async onFileChange(event) {
      const file = event.target.files[0];
      await this.handleFile(file);
      event.target.value = "";
    },

    async handleFile(file) {
      if (!file) {
        return;
      }

      if (!/\.(xlsx|xls)$/i.test(file.name || "")) {
        this.resetPreview();
        this.errorMessage = "รองรับเฉพาะไฟล์ .xlsx และ .xls";
        return;
      }

      this.fileName = file.name;
      this.errorMessage = "";
      this.importErrorSummary = "";

      try {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet, {
          defval: "",
          raw: false,
        });

        this.buildPreview(rows);
      } catch (error) {
        this.resetPreview();
        this.errorMessage = "ไม่สามารถอ่านไฟล์ Excel นี้ได้";
      }
    },

    buildPreview(rows) {
      this.resetPreview();

      const duplicateTracker = new Set();

      rows.forEach((row, index) => {
        const normalized = this.normalizeRow(row, index);

        if (!normalized.employeeCode) {
          this.skippedItems.push({
            ...normalized,
            reason: "ไม่มีรหัสบุคลากร",
          });
          return;
        }

        if (normalized.positionStatus !== "O : เปิด") {
          this.skippedItems.push({
            ...normalized,
            reason: "สถานะตำแหน่งไม่พร้อมใช้งาน",
          });
          return;
        }

        if (!normalized.firstName && !normalized.lastName) {
          this.skippedItems.push({
            ...normalized,
            reason: "ไม่มีชื่อและนามสกุล",
          });
          return;
        }

        if (duplicateTracker.has(normalized.employeeCode)) {
          this.duplicateItems.push({
            ...normalized,
            reason: "รหัสบุคลากรซ้ำในไฟล์",
          });
          return;
        }

        duplicateTracker.add(normalized.employeeCode);
        this.readyItems.push(normalized);
      });

      this.summary = {
        totalRows: rows.length,
        readyRows: this.readyItems.length,
        duplicateRows: this.duplicateItems.length,
        skippedRows: this.skippedItems.length,
      };
    },

    confirmImport() {
      if (this.readyItems.length === 0 || this.importing) {
        return;
      }

      this.importing = true;
      this.errorMessage = "";
      this.importSummary = null;
      this.importErrorSummary = "";

      Service.importAccounts({
        items: this.readyItems.map((item) => ({
          employeeCode: item.employeeCode,
          prefix: item.prefix,
          firstName: item.firstName,
          lastName: item.lastName,
          password: item.employeeCode,
          positionCode: item.positionCode,
          positionName: item.positionName,
          lineTypeCode: item.lineType,
          lineTypeName: item.lineTypeName,
          employeeTypeCode: item.employeeType,
          employeeTypeName: item.employeeTypeName,
          groupName: item.groupName,
          organizationName: item.organizationName,
          departmentName: item.departmentName,
          affiliationCode: item.organizationCode,
          affiliationName: item.affiliationName,
          employmentStatusCode: item.employmentStatus,
          startDate: item.startDate,
          endDate: item.endDate,
        })),
      })
        .then((response) => {
          var summary = (((response || {}).data || {}).data) || {};
          this.importSummary = summary;
          this.importErrorSummary = this.buildImportErrorSummary(summary);
        })
        .catch((error) => {
          this.errorMessage = this.extractImportErrorMessage(error);
          this.importErrorSummary = this.errorMessage;
        })
        .finally(() => {
          this.importing = false;
        });
    },

    normalizeRow(row, index) {
      const employeeCode = this.getRowValue(row, ["รหัสบุคลากร"]);
      const prefix = this.getRowValue(row, ["คำนำหน้า", "คำนำหน้านาม"]);
      const firstName = this.getRowValue(row, ["ชื่อ"]);
      const lastName = this.getRowValue(row, ["นามสกุล", "สกุล"]);
      const fullName = [prefix, firstName, lastName].filter(Boolean).join(" ");

      return {
        index: index + 1,
        employeeCode,
        prefix,
        firstName,
        lastName,
        fullName,
        positionCode: this.getRowValue(row, ["รหัสตำแหน่ง"]),
        positionStatus: this.getRowValue(row, ["สถานะของตำแหน่ง", "สถานะตำแหน่ง", "สถานะ"]),
        positionName: this.getRowValue(row, ["ชื่อตำแหน่ง", "ตำแหน่งงาน", "ตำแหน่ง"]),
        academicTitle: this.getRowValue(row, ["ตำแหน่งทางวิชาการ"]),
        lineType: this.getRowValue(row, ["สายงาน"]),
        lineTypeCode: this.getRowValue(row, ["สายงาน"]),
        lineTypeName: this.getRowValue(row, ["__EMPTY"]),
        employeeType: this.getRowValue(row, ["ประเภทบุคลากร"]),
        employeeTypeCode: this.getRowValue(row, ["ประเภทบุคลากร"]),
        employeeTypeName: this.getRowValue(row, ["__EMPTY_1"]),
        groupName: this.getRowValue(row, ["กลุ่มหน่วยงาน"]),
        organizationName: this.getRowValue(row, ["หน่วยงาน"]),
        organizationCode: this.getRowValue(row, ["หน่วยงาน", "รหัสหน่วยงาน"]),
        affiliationName: this.getRowValue(row, ["__EMPTY_2", "ชื่อหน่วยงาน"]),
        departmentName: this.getRowValue(row, ["สาขาวิชา/ฝ่ายงาน"]),
        employmentStatus: this.getRowValue(row, ["สถานภาพ"]),
        startDate: this.getRowValue(row, ["วันบรรจุ"]),
        endDate: this.getRowValue(row, ["วันหมดสัญญา"]),
      };
    },

    getRowValue(row, aliases) {
      var normalizedAliases = (aliases || []).map((alias) => this.cleanValue(alias));
      var rowKeys = Object.keys(row || {});

      for (var index = 0; index < normalizedAliases.length; index += 1) {
        var alias = normalizedAliases[index];

        if (Object.prototype.hasOwnProperty.call(row || {}, alias)) {
          return this.cleanValue(row[alias]);
        }

        var matchedKey = rowKeys.find((key) => this.cleanValue(key) === alias);
        if (matchedKey) {
          return this.cleanValue(row[matchedKey]);
        }
      }

      return "";
    },

    cleanValue(value) {
      if (value === null || value === undefined) {
        return "";
      }

      return String(value).trim();
    },

    getImportStatusLabel(status) {
      if (status === "success") {
        return "สำเร็จ";
      }

      if (status === "duplicate") {
        return "ซ้ำ/อัปเดต";
      }

      if (status === "failed") {
        return "ผิดพลาด";
      }

      return this.cleanValue(status) || "-";
    },

    formatImportResultReason(item) {
      var details = Array.isArray((item || {}).details) ? item.details : [];
      var reason = this.cleanValue((item || {}).reason);
      var detailText = details
        .map((detail) => {
          var label = this.cleanValue(detail.label || detail.field || "ข้อมูล");
          var value = this.cleanValue(detail.value);
          var message = this.cleanValue(detail.message);
          var parts = [label];

          if (value) {
            parts.push('ค่า "' + value + '"');
          }

          if (message) {
            parts.push(message);
          }

          return parts.join(" ");
        })
        .filter(Boolean)
        .join(" | ");

      if (reason === "existing-account-updated") {
        return "พบ account เดิมในระบบและอัปเดตข้อมูลให้แล้ว";
      }

      if (reason === "missing-employee-code") {
        return "ไม่พบรหัสบุคลากร";
      }

      if (reason && detailText && !reason.includes(detailText)) {
        return reason + " | " + detailText;
      }

      if (reason) {
        return reason;
      }

      if (detailText) {
        return detailText;
      }

      return "-";
    },

    buildImportErrorSummary(summary) {
      var failedItems = Array.isArray((summary || {}).items)
        ? summary.items.filter((item) => item.status === "failed")
        : [];

      if (failedItems.length === 0) {
        return "";
      }

      return failedItems
        .slice(0, 5)
        .map((item) => {
          var employeeCode = this.cleanValue(item.employeeCode) || "ไม่ทราบรหัสบุคลากร";
          return employeeCode + ": " + this.formatImportResultReason(item);
        })
        .join(" | ");
    },

    extractImportErrorMessage(error) {
      var responseData = (((error || {}).response || {}).data) || {};
      var detail = responseData.description
        || responseData.message
        || this.cleanValue(((responseData.error || {}).message))
        || this.cleanValue(error.message);

      var detailItems = Array.isArray(responseData.data && responseData.data.items)
        ? responseData.data.items.filter((item) => item.status === "failed")
        : [];

      if (detailItems.length > 0) {
        return this.buildImportErrorSummary({ items: detailItems });
      }

      if (detail) {
        return "ไม่สามารถ import account ได้: " + detail;
      }

      return "ไม่สามารถ import account ได้";
    },

    filterItems(items) {
      var keyword = this.cleanValue(this.searchText).toLowerCase();

      if (!keyword) {
        return items;
      }

      return items.filter((item) => {
        return [
          item.employeeCode,
          item.fullName,
          item.positionName,
          item.groupName,
          item.organizationName,
          item.departmentName,
          item.reason,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(keyword));
      });
    },

    resetPreview() {
      this.readyItems = [];
      this.duplicateItems = [];
      this.skippedItems = [];
      this.importSummary = null;
      this.importErrorSummary = "";
      this.searchText = "";
      this.summary = {
        totalRows: 0,
        readyRows: 0,
        duplicateRows: 0,
        skippedRows: 0,
      };
    },
  },
};
</script>

<style scoped>
.import-dropzone {
  border: 2px dashed #39f;
  border-radius: 1rem;
  padding: 2rem 1.25rem;
  text-align: center;
  cursor: pointer;
  background: rgba(51, 153, 255, 0.06);
}

.import-dropzone:hover {
  background: rgba(51, 153, 255, 0.1);
}

.import-dropzone.is-dragging {
  border-color: #2eb85c;
  background: rgba(46, 184, 92, 0.12);
  box-shadow: inset 0 0 0 1px rgba(46, 184, 92, 0.2);
}

.summary-card {
  background: #f8fafc;
  border-radius: 1rem;
}

.import-note {
  background: #f8fafc;
  border-radius: 1rem;
}
</style>
